import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Typography, 
  Input, 
  Select, 
  Form, 
  Tag, 
  Space, 
  Popconfirm, 
  message, 
  Tabs, 
  List, 
  Avatar, 
  Row, 
  Col,
  Badge,
  Divider,
  Pagination,
  Empty,
  Spin
} from 'antd';
import { 
  TeamOutlined, 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  PhoneOutlined, 
  UserOutlined,
  CalendarOutlined,
  MessageOutlined,
  ArrowLeftOutlined,
  StarOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { contactsConfig } from '../config/contacts';
import { useAuthStore } from '../stores/authStore';
import axios from 'axios';

// 配置axios baseURL
axios.defaults.baseURL = (typeof window !== 'undefined' ? window.location.protocol + '//' + window.location.host : '') + '/api';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const Contacts = () => {
  const navigate = useNavigate();
  const { initialize } = useAuthStore();
  const [contacts, setContacts] = useState([]);
  const [contactRecords, setContactRecords] = useState({});
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [showFriendlinessModal, setShowFriendlinessModal] = useState(false);
  const [currentContact, setCurrentContact] = useState(null);
  const [activeTab, setActiveTab] = useState('contacts');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [relationshipFilter, setRelationshipFilter] = useState('');
  const [form] = Form.useForm();
  const [recordForm] = Form.useForm();
  
  // 分页状态
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  
  // 统计信息
  const [statistics, setStatistics] = useState({
    totalContacts: 0,
    totalRecords: 0
  });

  // 加载联系人数据
  const fetchContacts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.current,
        limit: pagination.pageSize
      });
      
      if (searchKeyword) {
        params.append('search', searchKeyword);
      }
      
      if (relationshipFilter) {
        params.append('relationship', relationshipFilter);
      }
      
      const response = await axios.get(`${contactsConfig.apiEndpoints.contacts}?${params}`);
      const data = response.data;
      if (data.success) {
        setContacts(data.contacts || []);
        setPagination(prev => ({
          ...prev,
          total: data.pagination?.total || data.contacts?.length || 0
        }));
        setStatistics(prev => ({
          ...prev,
          totalContacts: data.pagination?.total || data.contacts?.length || 0
        }));
        
        // 获取联系记录总数
        fetchTotalRecords();
      } else {
        message.error(data.message || '获取联系人失败');
      }
    } catch (error) {
      console.error('获取联系人失败:', error);
      message.error('获取联系人失败');
    } finally {
      setLoading(false);
    }
  };

  // 加载联系记录
  const fetchContactRecords = async (contactId) => {
    try {
      const response = await axios.get(`${contactsConfig.apiEndpoints.records}/${contactId}`);
    const data = response.data;
      if (data.success) {
        setContactRecords(prev => ({
          ...prev,
          [contactId]: data.records || []
        }));
      }
    } catch (error) {
      console.error('获取联系记录失败:', error);
    }
  };

  // 获取联系记录总数
  const fetchTotalRecords = async () => {
    try {
      const response = await axios.get(`${contactsConfig.apiEndpoints.stats}`);
      const data = response.data;
      if (data.success) {
        setStatistics(prev => ({
          ...prev,
          totalRecords: data.data || 0
        }));
      }
    } catch (error) {
      console.error('获取联系记录统计失败:', error);
    }
  };

  // 加载所有联系记录
  const fetchAllRecords = async () => {
    for (const contact of contacts) {
      await fetchContactRecords(contact.id);
    }
  };

  // 分页处理
  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  // 搜索处理
  const handleSearch = (value) => {
    setSearchKeyword(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // 重置搜索
  const handleResetSearch = () => {
    setSearchKeyword('');
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // 添加联系人
  const handleAdd = () => {
    setCurrentContact(null);
    form.resetFields();
    setShowAddModal(true);
  };


  // 编辑联系人
  const handleEdit = (contact) => {
    setCurrentContact(contact);
    form.setFieldsValue(contact);
    setShowEditModal(true);
  };

  // 删除联系人
  const handleDelete = async (contactId) => {
    try {
      const response = await axios.delete(`${contactsConfig.apiEndpoints.delete}/${contactId}`);
    const data = response.data;
      if (data.success) {
        message.success('删除成功');
        fetchContacts();
      } else {
        message.error(data.message || '删除失败');
      }
    } catch (error) {
      console.error('删除联系人失败:', error);
      message.error('删除失败');
    }
  };

  // 保存联系人
  const handleSaveContact = async (values) => {
    try {
      let response;
      if (currentContact) {
        response = await axios.put(`${contactsConfig.apiEndpoints.update}/${currentContact.id}`, values);
      } else {
        response = await axios.post(contactsConfig.apiEndpoints.create, values);
      }

      if (response.data.success) {
        message.success(currentContact ? '更新成功' : '添加成功');
        setShowAddModal(false);
        setShowEditModal(false);
        fetchContacts();
      } else {
        message.error(response.data.message || '操作失败');
      }
    } catch (error) {
      console.error('保存联系人失败:', error);
      message.error('操作失败');
    }
  };

  // 查看联系记录
  const handleViewRecords = (contactId) => {
    const contact = contacts.find(c => c.id === contactId);
    setCurrentContact(contact);
    setShowRecordModal(true);
  };

  // 添加联系记录
  const handleAddRecord = (contact) => {
    setCurrentContact(contact);
    recordForm.resetFields();
    setShowRecordModal(true);
  };

  // 保存联系记录
  const handleSaveRecord = async (values) => {
    try {
      const response = await axios.post(contactsConfig.apiEndpoints.addRecord, {
        contact_id: currentContact.id,
        ...values
      });

      if (response.data.success) {
        message.success('记录添加成功');
        recordForm.resetFields();
        fetchContactRecords(currentContact.id);
      } else {
        message.error(response.data.message || '添加失败');
      }
    } catch (error) {
      console.error('添加联系记录失败:', error);
      message.error('添加失败');
    }
  };

  // 删除联系记录
  const handleDeleteRecord = async (recordId) => {
    try {
      const response = await axios.delete(`${contactsConfig.apiEndpoints.deleteRecord}/${recordId}`);
    const data = response.data;
      if (data.success) {
        message.success('记录删除成功');
        fetchContactRecords(currentContact.id);
      } else {
        message.error(data.message || '删除失败');
      }
    } catch (error) {
      console.error('删除联系记录失败:', error);
      message.error('删除失败');
    }
  };

  // 管理友好度
  const handleManageFriendliness = () => {
    setShowFriendlinessModal(true);
  };


  // 清空搜索
  const handleClearSearch = () => {
    setSearchKeyword('');
    setFilteredContacts(contacts);
  };

  // 关系筛选处理
  const handleRelationshipFilter = (value) => {
    console.log('🔍 前端关系筛选:', value);
    setRelationshipFilter(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // 清除关系筛选
  const handleClearRelationshipFilter = () => {
    setRelationshipFilter('');
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // 组件加载时获取数据
  useEffect(() => {
    initialize();
    fetchContacts();
  }, [pagination.current, pagination.pageSize, searchKeyword, relationshipFilter, initialize]);

  // 当contacts数据更新时，同步更新filteredContacts
  useEffect(() => {
    setFilteredContacts(contacts);
  }, [contacts]);

  // 注释掉自动加载所有联系记录，改为按需加载
  // useEffect(() => {
  //   if (contacts.length > 0) {
  //     fetchAllRecords();
  //   }
  // }, [contacts]);

  return (
    <div className="mod-page">
      {/* 页头 */}
      <div className="mod-header">
        <div>
          <div className="mod-header-title">
            <TeamOutlined style={{ marginRight: '10px', color: contactsConfig.bgColor }} />
            {contactsConfig.name}
          </div>
          <div className="mod-header-subtitle">
            管理你的人脉网络，记录联系往来，维护每一段珍贵的关系
          </div>
        </div>
        <Space wrap>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/')}
          >
            返回
          </Button>
          <Button
            icon={<StarOutlined />}
            onClick={handleManageFriendliness}
          >
            友好度管理
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
            style={{ backgroundColor: contactsConfig.bgColor, borderColor: contactsConfig.bgColor }}
          >
            添加朋友
          </Button>
        </Space>
      </div>

      {/* 统计信息 */}
      <div className="mod-stats">
        <div className="mod-stat-card">
          <div className="mod-stat-icon" style={{ color: contactsConfig.bgColor }}>
            <TeamOutlined />
          </div>
          <div>
            <div className="mod-stat-value">{statistics.totalContacts}</div>
            <div className="mod-stat-label">总联系人</div>
          </div>
        </div>
        <div className="mod-stat-card">
          <div className="mod-stat-icon" style={{ color: '#13c2c2' }}>
            <MessageOutlined />
          </div>
          <div>
            <div className="mod-stat-value">{statistics.totalRecords}</div>
            <div className="mod-stat-label">总联系记录</div>
          </div>
        </div>
        <div className="mod-stat-card">
          <div className="mod-stat-icon" style={{ color: '#faad14' }}>
            <UserOutlined />
          </div>
          <div>
            <div className="mod-stat-value">{pagination.total}</div>
            <div className="mod-stat-label">当前显示</div>
          </div>
        </div>
      </div>

      {/* 搜索/筛选工具栏 */}
      <Card className="mod-toolbar" bodyStyle={{ padding: 12 }}>
        <div className="mod-toolbar-row">
          <Input.Search
            placeholder="搜索姓名、关系、手机号、备注..."
            value={searchKeyword}
            onChange={(e) => handleSearch(e.target.value)}
            onSearch={handleSearch}
            onClear={handleClearSearch}
            allowClear
            style={{ maxWidth: 320, flex: '1 1 240px' }}
            enterButton
          />
          <Select
            placeholder="按关系筛选"
            value={relationshipFilter}
            onChange={handleRelationshipFilter}
            allowClear
            onClear={handleClearRelationshipFilter}
            style={{ width: 160 }}
          >
            {contactsConfig.relationshipTypes.map(type => (
              <Option key={type} value={type}>{type}</Option>
            ))}
          </Select>
        </div>
      </Card>

      {/* 主要内容 */}
      <div>
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          items={[
            {
              key: 'contacts',
              label: '朋友列表',
              children: (
                <Card bodyStyle={{ padding: 16 }} style={{ borderRadius: 16 }}>
                  {loading ? (
                    <div style={{ textAlign: 'center', padding: '60px' }}>
                      <Spin />
                    </div>
                  ) : filteredContacts.length === 0 ? (
                    <Empty
                      style={{ padding: '48px 0' }}
                      description={searchKeyword ? `没有找到包含"${searchKeyword}"的联系人` : '暂无联系人，点击"添加朋友"按钮开始添加'}
                    >
                      {!searchKeyword && (
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          onClick={handleAdd}
                          style={{ backgroundColor: contactsConfig.bgColor, borderColor: contactsConfig.bgColor }}
                        >
                          添加朋友
                        </Button>
                      )}
                    </Empty>
                  ) : (
                    <Row gutter={[16, 16]}>
                      {filteredContacts.map((contact) => {
                        const records = contactRecords[contact.id] || [];
                        return (
                          <Col xs={24} sm={12} md={8} lg={6} key={contact.id}>
                            <div className="mod-card">
                              {/* 头部：头像 + 姓名 + 关系 */}
                              <div className="mod-card-head">
                                <div
                                  className="mod-card-icon"
                                  style={{ background: `${contactsConfig.bgColor}1a`, color: contactsConfig.bgColor }}
                                >
                                  {contact.name ? contact.name.charAt(0) : <UserOutlined />}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div className="mod-card-title">{contact.name}</div>
                                  <div className="mod-card-sub">
                                    {contact.phone ? (
                                      <a
                                        href={`tel:${contact.phone}`}
                                        style={{
                                          color: contactsConfig.bgColor,
                                          textDecoration: 'none',
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: '4px'
                                        }}
                                      >
                                        <PhoneOutlined />
                                        {contact.phone}
                                      </a>
                                    ) : (
                                      <Text type="secondary" style={{ fontSize: '13px' }}>无电话</Text>
                                    )}
                                  </div>
                                </div>
                              </div>

                              <div className="mod-card-body">
                                <div className="mod-badges">
                                  {contact.relationship && (
                                    <span className="mod-badge mod-badge-primary">{contact.relationship}</span>
                                  )}
                                  {contact.profession && (
                                    <span className="mod-badge">{contact.profession}</span>
                                  )}
                                </div>
                                {contact.notes && (
                                  <div style={{ marginTop: '8px' }}>
                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                      备注: {contact.notes}
                                    </Text>
                                  </div>
                                )}
                              </div>

                              <Divider style={{ margin: '10px 0' }} />

                              {/* 操作行：联系记录、编辑、添加记录、删除 */}
                              <div className="mod-card-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Badge count={records.length} size="small">
                                  <Button
                                    type="text"
                                    size="small"
                                    icon={<MessageOutlined />}
                                    onClick={() => handleViewRecords(contact.id)}
                                  >
                                    记录
                                  </Button>
                                </Badge>
                                <Space size={0}>
                                  <Button
                                    type="text"
                                    size="small"
                                    icon={<EditOutlined />}
                                    onClick={() => handleEdit(contact)}
                                    title="编辑"
                                  />
                                  <Button
                                    type="text"
                                    size="small"
                                    icon={<PlusOutlined />}
                                    onClick={() => handleAddRecord(contact)}
                                    title="添加记录"
                                  />
                                  <Popconfirm
                                    title="确定要删除这个联系人吗？"
                                    onConfirm={() => handleDelete(contact.id)}
                                    okText="确定"
                                    cancelText="取消"
                                  >
                                    <Button
                                      type="text"
                                      size="small"
                                      danger
                                      icon={<DeleteOutlined />}
                                      title="删除"
                                    />
                                  </Popconfirm>
                                </Space>
                              </div>
                            </div>
                          </Col>
                        );
                      })}
                    </Row>
                  )}
                  
                  {/* 分页组件 */}
                  {pagination.total > 0 && (
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      marginTop: '24px',
                      padding: '16px 0'
                    }}>
                      <Pagination
                        current={pagination.current}
                        pageSize={pagination.pageSize}
                        total={pagination.total}
                        showSizeChanger
                        showQuickJumper
                        showTotal={(total, range) => 
                          `第 ${range[0]}-${range[1]} 条，共 ${total} 条`
                        }
                        onChange={(page, pageSize) => {
                          setPagination(prev => ({
                            ...prev,
                            current: page,
                            pageSize: pageSize || prev.pageSize
                          }));
                        }}
                        onShowSizeChange={(current, size) => {
                          setPagination(prev => ({
                            ...prev,
                            current: 1,
                            pageSize: size
                          }));
                        }}
                      />
                    </div>
                  )}
                </Card>
              )
            },
            {
              key: 'tips',
              label: '关系技巧',
              children: (
                <Card title={contactsConfig.relationshipTips.title}>
                  {contactsConfig.relationshipTips.sections.map((section, index) => (
                    <div key={index} style={{ marginBottom: '24px' }}>
                      <Title level={4} style={{ color: contactsConfig.bgColor }}>
                        {section.title}
                      </Title>
                      <ul style={{ paddingLeft: '20px' }}>
                        {section.items.map((item, itemIndex) => (
                          <li key={itemIndex} style={{ marginBottom: '8px' }}>
                            <Text>{item}</Text>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </Card>
              )
            },
            {
              key: 'advice',
              label: '管理建议',
              children: (
                <Card title={contactsConfig.networkAdvice.title}>
                  {contactsConfig.networkAdvice.sections.map((section, index) => (
                    <div key={index} style={{ marginBottom: '24px' }}>
                      <Title level={4} style={{ color: contactsConfig.bgColor }}>
                        {section.title}
                      </Title>
                      <ul style={{ paddingLeft: '20px' }}>
                        {section.items.map((item, itemIndex) => (
                          <li key={itemIndex} style={{ marginBottom: '8px' }}>
                            <Text>{item}</Text>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </Card>
              )
            }
          ]}
        />
      </div>

      {/* 添加/编辑联系人弹窗 */}
      {(showAddModal || showEditModal) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 999999,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '600px',
            maxWidth: '90vw',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600' }}>
              {currentContact ? '编辑朋友' : '添加朋友'}
            </h3>
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSaveContact}
            >
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="name"
                    label="姓名"
                    rules={[{ required: true, message: '请输入姓名' }]}
                  >
                    <Input placeholder="请输入姓名" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="relationship"
                    label="关系"
                    rules={[{ required: true, message: '请选择关系' }]}
                  >
                    <select
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d9d9d9',
                        borderRadius: '6px',
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                      value={form.getFieldValue('relationship') || ''}
                      onChange={(e) => form.setFieldsValue({ relationship: e.target.value })}
                    >
                      <option value="">请选择关系</option>
                      {contactsConfig.relationshipTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </Form.Item>
                </Col>
              </Row>
              
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="profession"
                    label="职业"
                  >
                    <select
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d9d9d9',
                        borderRadius: '6px',
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                      value={form.getFieldValue('profession') || ''}
                      onChange={(e) => form.setFieldsValue({ profession: e.target.value })}
                    >
                      <option value="">请选择职业</option>
                      {contactsConfig.professionTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="friendliness"
                    label="友好度"
                    rules={[{ required: true, message: '请选择友好度' }]}
                  >
                    <select
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #d9d9d9',
                        borderRadius: '6px',
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                      value={form.getFieldValue('friendliness') || ''}
                      onChange={(e) => form.setFieldsValue({ friendliness: e.target.value })}
                    >
                      <option value="">请选择友好度</option>
                      {contactsConfig.friendlinessLevels.map(level => (
                        <option key={level.value} value={level.value}>{level.label}</option>
                      ))}
                    </select>
                  </Form.Item>
                </Col>
              </Row>
              
              <Form.Item
                name="phone"
                label="手机号"
              >
                <Input placeholder="请输入手机号" />
              </Form.Item>
              
              <Form.Item
                name="resources"
                label="资源"
              >
                <TextArea rows={3} placeholder="请输入资源信息" />
              </Form.Item>
              
              <Form.Item
                name="notes"
                label="备注"
              >
                <TextArea rows={3} placeholder="请输入备注信息" />
              </Form.Item>
              
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
                <Button onClick={() => {
                  setShowAddModal(false);
                  setShowEditModal(false);
                  form.resetFields();
                }}>
                  取消
                </Button>
                <Button type="primary" htmlType="submit">
                  {currentContact ? '更新' : '添加'}
                </Button>
              </div>
            </Form>
          </div>
        </div>
      )}

      {/* 联系记录弹窗 */}
      {showRecordModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 999999,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '800px',
            maxWidth: '90vw',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600' }}>
              {currentContact?.name || ''} - 联系记录
            </h3>
        <div style={{ marginBottom: '16px' }}>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={() => recordForm.resetFields()}
            style={{ backgroundColor: contactsConfig.bgColor, borderColor: contactsConfig.bgColor }}
          >
            添加记录
          </Button>
        </div>
        
        <Form
          form={recordForm}
          layout="vertical"
          onFinish={handleSaveRecord}
        >
          <Form.Item
            name="content"
            label="联系内容"
            rules={[{ required: true, message: '请输入联系内容' }]}
          >
            <TextArea rows={4} placeholder="请输入联系内容" />
          </Form.Item>
          
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                保存记录
              </Button>
              <Button onClick={() => recordForm.resetFields()}>
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
        
        <div style={{ marginTop: '24px' }}>
          <Title level={5}>历史记录</Title>
          <List
            dataSource={currentContact ? (contactRecords[currentContact.id] || []) : []}
            renderItem={(record) => (
              <List.Item
                actions={[
                  <Popconfirm
                    title="确定要删除这条记录吗？"
                    onConfirm={() => handleDeleteRecord(record.id)}
                    okText="确定"
                    cancelText="取消"
                  >
                    <Button type="link" danger size="small">
                      删除
                    </Button>
                  </Popconfirm>
                ]}
              >
                <List.Item.Meta
                  title={
                    <Space>
                      <CalendarOutlined />
                      <Text>{new Date(record.created_at).toLocaleString('zh-CN')}</Text>
                    </Space>
                  }
                  description={record.content}
                />
              </List.Item>
            )}
          />
        </div>
          </div>
        </div>
      )}

      {/* 友好度管理弹窗 */}
      {showFriendlinessModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 999999,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '600px',
            maxWidth: '90vw',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600' }}>
              友好度管理
            </h3>
            <div>
              {contactsConfig.friendlinessLevels.map((level, index) => (
                <Card key={level.value} size="small" style={{ marginBottom: '12px' }}>
                  <Row gutter={16} align="middle">
                    <Col span={6}>
                      <Tag color={level.color} style={{ fontSize: '14px', padding: '4px 8px' }}>
                        {level.label}
                      </Tag>
                    </Col>
                    <Col span={18}>
                      <Text>{level.description}</Text>
                    </Col>
                  </Row>
                </Card>
              ))}
              
              <div style={{ marginTop: '16px' }}>
                <Title level={5}>联系频率建议</Title>
                {Object.entries(contactsConfig.contactFrequency).map(([key, value]) => (
                  <div key={key} style={{ marginBottom: '8px' }}>
                    <Text strong>{value.label}</Text>: {value.frequency} - {value.description}
                  </div>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
              <Button onClick={() => setShowFriendlinessModal(false)}>
                关闭
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contacts;
